<script setup lang="ts">
import type { EventVault } from "@/models"
import { useQuery } from "@tanstack/vue-query"
import { useLocalStorage } from "@vueuse/core"
import { computed } from "vue"
import { getExtendEventPricing, useCreateExtendContactRequest } from "@/api"
import { Button, ButtonLink, Card, Chip, Icon } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useTranslation } from "@/composables"
import { EventVaultStatus, ProductTypes } from "@/models"

interface EventExtendCardProps {
  eventVault?: EventVault
}

const props = defineProps<EventExtendCardProps>()

const contactSubmittedAt = useLocalStorage<Record<string, number>>("extend-contact-submitted", {})

const { t } = useTranslation()
const toast = useToast()

const { data: eventExtendPricing, isLoading: isPricingLoading } = useQuery({
  queryKey: ["getExtendEventPricing", props.eventVault],
  queryFn: () => getExtendEventPricing(props.eventVault?.id),
})

const { mutate: submitContactRequest, isPending: isSubmittingContact } = useCreateExtendContactRequest()

const hasExtendProduct = computed(() =>
  Boolean(eventExtendPricing.value?.gpxProductID && eventExtendPricing.value?.extendEventURL),
)

const contactSubmitted = computed(() => {
  const eventVaultId = props.eventVault?.id

  if (!eventVaultId)
    return false

  const submittedAt = contactSubmittedAt.value[eventVaultId]
  return Boolean(submittedAt && Date.now() - submittedAt < 14 * 24 * 60 * 60 * 1000)
})

function onContactUs() {
  const eventVaultId = props.eventVault?.id

  if (!eventVaultId || isSubmittingContact.value || contactSubmitted.value)
    return

  submitContactRequest(eventVaultId, {
    onSuccess: () => contactSubmittedAt.value = { ...contactSubmittedAt.value, [eventVaultId]: Date.now() },
    onError: () => toast.error(),
  })
}

const packageLabel = computed(() => ProductTypes.getLabel(props.eventVault?.productKey, props.eventVault?.productName))
</script>

<template>
  <div>
    <div v-if="isPricingLoading">
      <div class="gpx-skeleton h-28 w-full" />
      <div class="gpx-skeleton h-10 w-2/5 mt-2" />
    </div>

    <div v-else-if="hasExtendProduct" class="flex flex-col gap-5">
      <p>
        {{ t("event_extend_card.message") }}
      </p>

      <Card variant="pink-border" :with-padding="false">
        <div class="p-3 flex flex-col gap-5">
          <div class="flex justify-between gap-5">
            <div>
              <div class="font-comfortaa font-semibold">
                {{ eventVault?.title }}
              </div>
              <div class="text-sm">
                {{ t("event_extend_card.extend_time") }}
              </div>
            </div>
            <div class="font-semibold font-comfortaa">
              <div class="text-xl">
                {{ `${eventExtendPricing?.currencySymbol}${eventExtendPricing?.discountedPrice}` }}
              </div>
              <div class="line-through text-gray-400">
                {{ `${eventExtendPricing?.currencySymbol}${eventExtendPricing?.originalPrice}` }}
              </div>
            </div>
          </div>

          <div class="flex justify-between">
            <div class="flex gap-1 font-comfortaa items-center text-[#E58783] font-extrabold">
              <div>{{ packageLabel }}</div>
            </div>
            <Chip
              variant="pink-solid"
              :label="t('event_extend_card.discount_amount', { discount: eventExtendPricing?.discountPercentage })"
            />
          </div>
        </div>
      </Card>

      <ButtonLink :href="eventExtendPricing?.extendEventURL" class="w-fit">
        {{
          eventVault?.status === EventVaultStatus.Expired
            ? t("event_extend_card.extend_btn_expired", {
              discount: `${eventExtendPricing?.currencySymbol}${eventExtendPricing?.discountedPrice}`,
            })
            : t("event_extend_card.extend_btn_expired_soon")
        }}
      </ButtonLink>
    </div>

    <Card v-else class="border border-gray-200">
      <Transition name="gpx-fade" mode="out-in">
        <p v-if="contactSubmitted" key="success">
          {{ t("event_extend_card.contact_us_success") }}
        </p>
        <div v-else key="request" class="flex flex-col gap-4">
          <p>{{ t("event_extend_card.contact_us_message") }}</p>

          <Button
            class="w-fit"
            :disabled="isSubmittingContact"
            data-test="extend_contact_us_button"
            @click="onContactUs"
          >
            {{ t("event_extend_card.contact_us_button") }}
            <Icon v-if="isSubmittingContact" icon="Loading" />
          </Button>
        </div>
      </Transition>
    </Card>
  </div>
</template>
